import {FieldValues} from 'platform/components';
import {match} from 'ts-pattern';

import {always, indexBy, isNotNil} from 'ramda';
import {isDate, isObject} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  DefinitionValueType,
  NullableFieldValue,
  useGetFieldDefinitionListQuery,
} from '@dms/api/settings';
import {EntityResourceIds, TypedAnyValue} from '@dms/api/shared';

import {getApiDateString, getApiDateTimeString, getSafely} from 'shared/web';

import {DEFAULT_CURRENCY} from '../../../constants/currency';
import {areFormFieldsDifferent} from '../../../utils/areFormFieldsDifferent';
import {resolveCustomFieldBooleanValue} from '../utils/resolveCustomFieldBooleanValue';

type GetSubmitFieldValuesProps = {resourceId: EntityResourceIds};

export const useGetSubmitFieldValues = ({resourceId}: GetSubmitFieldValuesProps) => {
  const {data: tenant} = useGetTenantQuery();
  const {data: definitions} = useGetFieldDefinitionListQuery({
    resourceId,
    type: 'tenant',
  });
  const definitionsById = indexBy((item) => item.id, definitions ?? []);

  const getFieldValues = (values: FieldValues, previousValues: FieldValues) =>
    Object.entries(values)
      .map(([key, value]) => {
        const valueType = getSafely(definitionsById, key)?.valueType;
        const previousValue = getSafely(previousValues, key);

        if (!valueType) {
          return null;
        }

        const formattedValue = getFormattedValue(
          value,
          valueType,
          tenant?.currency ?? DEFAULT_CURRENCY
        );

        const formattedPreviousValue = getFormattedValue(
          previousValue,
          valueType,
          tenant?.currency ?? DEFAULT_CURRENCY
        );

        const hasValueChanged = getHasValueChanged(
          formattedValue,
          formattedPreviousValue,
          valueType
        );

        if (!hasValueChanged) {
          return null;
        }

        return {
          definitionId: key,
          value:
            formattedValue === null
              ? null
              : {
                  type: valueType,
                  value: formattedValue,
                },
        } as NullableFieldValue;
      })
      .filter(Boolean);

  return [getFieldValues];
};

const getHasValueChanged = (
  formattedValue: TypedAnyValue['value'],
  formattedPreviousValue: TypedAnyValue['value'],
  valueType: DefinitionValueType
): boolean => {
  if (valueType === 'money') {
    // custom handling for money type because it's the only type that is an object
    if (formattedValue === null && formattedPreviousValue === null) {
      return false;
    }
    if (formattedValue === null || formattedPreviousValue === null) {
      return true;
    }
    if (isObject(formattedValue) && isObject(formattedPreviousValue)) {
      const currentMoney = formattedValue as {amount: string; currency: string};
      const previousMoney = formattedPreviousValue as {amount: string; currency: string};
      return (
        currentMoney.amount !== previousMoney.amount ||
        currentMoney.currency !== previousMoney.currency
      );
    }
  }

  return areFormFieldsDifferent(formattedValue, formattedPreviousValue);
};

const getFormattedValue = (
  value: TypedAnyValue['value'],
  valueType: DefinitionValueType,
  tenantCurrency: string
): TypedAnyValue['value'] =>
  match(valueType)
    .with('date', always(isDate(value) ? getApiDateString(value) : null))
    .with('datetime', always(isDate(value) ? getApiDateTimeString(value) : null))
    .with(
      'money',
      always(isNotNil(value) ? {amount: value.toString(), currency: tenantCurrency} : null)
    )
    .with('boolean', always(resolveCustomFieldBooleanValue(value)))
    .with('integer', always(value ?? null))
    .with('decimal', always(value?.toString() ?? null))
    .otherwise(always(value?.toString() ?? null));
